import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Paginated, type ProjectRef, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDate } from '../lib/format';
import { anyFilter, useKeptFilters } from '../lib/kept-filters';
import { WorkflowStep } from '../components/WorkflowChain';
import { ChecklistProgressChip } from '../components/TaskChecklist';
import { TaskHolder } from '../components/TaskHolder';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  RowLink,
  Select,
  Table,
  Td,
  Th,
  Tr,
  priorityTone,
  statusTone,
} from '../components/ui';

const STATUSES = ['TO_DO', 'IN_PROGRESS', 'BLOCKED', 'REVIEW', 'DONE', 'CANCELLED'];

/** The filters a link to the list may set: `/tasks?priority=URGENT&mine=true`. */
const LINKED_FILTERS = ['status', 'priority', 'mine', 'assignedByMe', 'overdue'];

export function TasksPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  // As the API's taskScope: without these, someone sees only the tasks given to them or to their
  // department, or made by them.
  const seesOthersTasks = can(P.TASKS_ASSIGN) || can(P.TASKS_MANAGE_ALL) || can(P.PROJECTS_VIEW_ALL);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  // A link may open the list filtered — the dashboard's urgent tasks open it at Urgent (2026-10-11).
  // Otherwise the list opens as it was left: filters are kept until cleared (2026-10-22).
  const [linked] = useState(() =>
    LINKED_FILTERS.some((key) => params.get(key))
      ? Object.fromEntries(LINKED_FILTERS.flatMap((key) => (params.get(key) ? [[key, params.get(key)!]] : [])))
      : undefined,
  );
  const [filters, setFilters] = useKeptFilters('tasks', linked);
  // Read once: from then on the filters are the page's, and the address no longer says what is shown.
  useEffect(() => {
    if (LINKED_FILTERS.some((key) => params.has(key))) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (key: string, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };
  // "My tasks" and "Assigned by me" look opposite ways — to me, by me — so choosing one lets go of the other.
  const toggleWhose = (key: 'mine' | 'assignedByMe') => {
    setFilters((f) => ({ ...f, mine: '', assignedByMe: '', [key]: f[key] === 'true' ? '' : 'true' }));
    setPage(1);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tasks', page, filters],
    queryFn: () => api.get<Paginated<any>>('/tasks', { page, pageSize: 25, ...filters }),
  });

  const { data: projects } = useQuery({
    queryKey: ['projects', 'options'],
    queryFn: () => api.get<ProjectRef[]>('/projects/options'),
  });
  const { data: users } = useQuery({
    queryKey: ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options'),
  });

  return (
    <>
      <PageHeader
        title={t('tasks.title')}
        subtitle={seesOthersTasks ? undefined : t('tasks.ownOnlyHint')}
        count={data?.total}
        action={
          can(P.TASKS_CREATE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/tasks/new')}>
              {t('tasks.new')}
            </Button>
          )
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Input placeholder={t('common.search')} value={filters.q ?? ''} onChange={(e) => setFilter('q', e.target.value)} />
          <Select value={filters.status ?? ''} onChange={(e) => setFilter('status', e.target.value)}>
            <option value="">{t('common.status')} — {t('common.all')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{enumLabel(s, locale)}</option>
            ))}
          </Select>
          <Select value={filters.priority ?? ''} onChange={(e) => setFilter('priority', e.target.value)}>
            <option value="">{t('common.priority')} — {t('common.all')}</option>
            {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => (
              <option key={p} value={p}>{enumLabel(p, locale)}</option>
            ))}
          </Select>
          <Select value={filters.projectId ?? ''} onChange={(e) => setFilter('projectId', e.target.value)}>
            <option value="">{t('common.project')} — {t('common.all')}</option>
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>{localized(p)}</option>
            ))}
          </Select>
          <Select value={filters.assigneeUserId ?? ''} onChange={(e) => setFilter('assigneeUserId', e.target.value)}>
            <option value="">{t('common.assignee')} — {t('common.all')}</option>
            {users?.map((u) => (
              <option key={u.id} value={u.id}>{localized(u)}</option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap gap-2 border-t hairline px-4 py-2.5">
          <Button size="sm" variant={filters.mine === 'true' ? 'primary' : 'ghost'} onClick={() => toggleWhose('mine')}>
            {t('kanban.myTasks')}
          </Button>
          <Button
            size="sm"
            variant={filters.assignedByMe === 'true' ? 'primary' : 'ghost'}
            onClick={() => toggleWhose('assignedByMe')}
          >
            {t('tasks.assignedByMe')}
          </Button>
          <Button
            size="sm"
            variant={filters.overdue === 'true' ? 'danger' : 'ghost'}
            onClick={() => setFilter('overdue', filters.overdue === 'true' ? '' : 'true')}
          >
            {t('tickets.overdue')}
          </Button>
          <Button
            size="sm"
            variant={filters.generalOnly === 'true' ? 'primary' : 'ghost'}
            onClick={() => setFilter('generalOnly', filters.generalOnly === 'true' ? '' : 'true')}
          >
            {t('common.general')}
          </Button>
          {anyFilter(filters) && (
            <Button size="sm" variant="ghost" onClick={() => { setFilters({}); setPage(1); }}>
              {t('common.clear')}
            </Button>
          )}
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<ClipboardList className="h-8 w-8" />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('common.project')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.priority')}</Th>
                  <Th>{t('common.assignee')}</Th>
                  <Th align="end">{t('common.dueDate')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((task) => {
                  const overdue =
                    task.dueAt && new Date(task.dueAt) < new Date() && !['DONE', 'CANCELLED'].includes(task.status);
                  return (
                    <Tr key={task.id} onClick={() => navigate(`/tasks/${task.id}`)}>
                      <Td className="max-w-sm">
                        <span className="flex items-center gap-2">
                          <RowLink to={`/tasks/${task.id}`} className="min-w-0 truncate">{task.title}</RowLink>
                          <ChecklistProgressChip progress={task.checklistProgress} />
                        </span>
                        <WorkflowStep task={task} className="max-w-full" />
                      </Td>
                      <Td className="text-muted">
                        {task.project ? localized(task.project) : <span className="text-faint">{t('common.general')}</span>}
                      </Td>
                      <Td>
                        <Badge tone={statusTone(task.status)}>{enumLabel(task.status, locale)}</Badge>
                      </Td>
                      <Td>
                        <Badge tone={priorityTone(task.priority)}>{enumLabel(task.priority, locale)}</Badge>
                      </Td>
                      <Td className="text-muted">
                        {/* A person, or everyone in the department that shares it (2026-10-10). */}
                        <TaskHolder task={task} />
                      </Td>
                      <Td align="end" className={overdue ? 'text-error-fg dark:text-error-dark tabular' : 'text-muted tabular'}>
                        {task.dueAt ? formatDate(task.dueAt, locale) : '—'}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>

    </>
  );
}
